import { useEffect, useState } from 'preact/hooks'
import { emailBlastApi } from '@/api/emailBlast'
import type { CampaignBlastSummaryDto, CampaignEmailRollupDto } from '@/types/emailBlast'
import { routes } from '@/utils/routes'
import { BlastStateBadge } from '@/components/webs/email-blasts/BlastStateBadge'
import { formatRate } from '@/components/webs/email-blasts/engagementFormat'
import { EmailEngagementTrendChart } from './EmailEngagementTrendChart'

interface Props {
  clientCompanyId: string
  workspaceId: string
  campaignId: string
}

const OPEN_RATE_CAVEAT =
  'Apple Mail Privacy auto-loads the open pixel, so open rate is inflated. Treat clicks as the reliable signal.'

/**
 * Campaign Broadcasts tab (ORB-384): the campaign-grain email rollup. A slim headline measure row leads,
 * then the daily sends/opens/clicks trend (clicks privileged), then a per-blast table drilling into each
 * blast's detail route. Marketing blasts only. Rates read "—" (never 0%) when there's nothing to divide by.
 */
export function CampaignBroadcastsTab({ clientCompanyId, workspaceId, campaignId }: Props) {
  const [data, setData] = useState<CampaignEmailRollupDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const ctrl = new AbortController()
    setLoading(true)
    setError(null)
    emailBlastApi.campaignRollup(workspaceId, campaignId, ctrl.signal)
      .then(res => {
        if (ctrl.signal.aborted) return
        if (res.data) setData(res.data)
        else setError(res.error || 'Failed to load broadcasts')
      })
      .finally(() => { if (!ctrl.signal.aborted) setLoading(false) })
    return () => ctrl.abort()
  }, [workspaceId, campaignId])

  if (loading && !data) return <div class="px-1 py-10 text-sm text-slate-500 dark:text-slate-400">Loading broadcasts…</div>
  if (error && !data) return <div class="px-1 py-10 text-sm text-red-600 dark:text-red-400">{error}</div>

  if (!data || data.BlastCount === 0) {
    return (
      <div class="px-6 py-16 text-center">
        <p class="text-sm font-medium text-slate-700 dark:text-slate-200">No broadcasts in this campaign yet</p>
        <p class="mx-auto mt-1 max-w-md text-sm text-slate-500 dark:text-slate-400">
          Email blasts and their open/click performance appear here once an activation in this campaign begins sending.
        </p>
      </div>
    )
  }

  const hasTrend = data.Trend.length > 0

  return (
    <div class="space-y-7">
      {/* headline measure row */}
      <div class="flex flex-wrap items-end gap-x-8 gap-y-4">
        <Measure value={data.BlastCount.toLocaleString()} label="blasts" />
        <Measure value={data.Sent.toLocaleString()} label="sent" />
        <Measure value={data.Delivered.toLocaleString()} label="delivered" />
        <Divider />
        <Measure value={formatRate(data.ClickRatePercent)} label="click rate" size="lg" privileged />
        <Measure value={formatRate(data.OpenRatePercent)} label="open rate" muted caveat={OPEN_RATE_CAVEAT} />
        <Divider />
        <Measure value={data.UnsubscribedCount.toLocaleString()} label="unsubscribed" />
      </div>

      {/* trend */}
      <div>
        <div class="mb-2 flex items-center justify-between gap-3">
          <h3 class="text-sm font-semibold text-slate-700 dark:text-slate-200">Over time</h3>
          <div class="flex items-center gap-4 text-xs">
            <Legend swatch="bar" label="Sends" />
            <Legend swatch="opens" label="Opens" />
            <Legend swatch="clicks" label="Clicks" />
          </div>
        </div>
        {hasTrend ? (
          <>
            <EmailEngagementTrendChart points={data.Trend} />
            <p class="text-xs text-slate-400 dark:text-slate-500">
              Each send, open and click is counted on its own day. Clicks are the reliable signal; opens are inflated by inbox privacy.
            </p>
          </>
        ) : (
          <div class="rounded-lg border border-dashed border-slate-300 dark:border-slate-700 px-5 py-10 text-center">
            <p class="text-sm font-medium text-slate-700 dark:text-slate-200">No engagement data yet</p>
            <p class="mx-auto mt-1 max-w-md text-sm text-slate-500 dark:text-slate-400">
              Send counts apply, but no tracked opens or clicks have landed yet. Rates show “—” until they do.
            </p>
          </div>
        )}
      </div>

      {/* per-blast table */}
      <div>
        <h3 class="mb-2 text-sm font-semibold text-slate-700 dark:text-slate-200">By blast</h3>
        <div class="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-800">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-slate-200 dark:border-slate-800 text-left text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500">
                <th class="px-4 py-2.5 font-semibold">Blast</th>
                <th class="hidden px-4 py-2.5 font-semibold sm:table-cell">State</th>
                <th class="px-4 py-2.5 font-semibold text-right">Sent</th>
                <th class="px-4 py-2.5 font-semibold text-right">Delivered</th>
                <th class="px-4 py-2.5 font-semibold text-right text-primary-700/70 dark:text-primary-400/70">Click %</th>
                <th class="px-4 py-2.5 font-semibold text-right">CTOR</th>
                <th class="px-4 py-2.5 font-semibold text-right">
                  <span class="underline decoration-dotted underline-offset-2 cursor-help" title={OPEN_RATE_CAVEAT}>Open % ⓘ</span>
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100 dark:divide-slate-800/70">
              {data.Blasts.map(blast => (
                <BlastRow key={blast.BlastId} blast={blast} clientCompanyId={clientCompanyId} workspaceId={workspaceId} campaignId={campaignId} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

function BlastRow({ blast, clientCompanyId, workspaceId, campaignId }: { blast: CampaignBlastSummaryDto; clientCompanyId: string; workspaceId: string; campaignId: string }) {
  const href = routes.scoped(clientCompanyId, workspaceId).webBroadcast(campaignId, blast.WebId, blast.BlastId)
  const hasData = blast.Delivered > 0
  return (
    <tr class="hover:bg-slate-50 dark:hover:bg-slate-800/50">
      <td class="px-4 py-2.5">
        <a href={href} class="font-medium text-primary-700 dark:text-primary-400 hover:underline">{formatSendDate(blast.SendAtAbsoluteUtc)}</a>
      </td>
      <td class="hidden px-4 py-2.5 sm:table-cell"><BlastStateBadge state={blast.State} /></td>
      <td class="px-4 py-2.5 text-right tabular-nums text-slate-600 dark:text-slate-300">{blast.Sent.toLocaleString()}</td>
      <td class="px-4 py-2.5 text-right tabular-nums text-slate-600 dark:text-slate-300">{blast.Delivered.toLocaleString()}</td>
      <td class="px-4 py-2.5 text-right tabular-nums font-medium text-primary-700 dark:text-primary-400">
        {hasData ? formatRate(blast.ClickRatePercent) : <span class="text-slate-400 dark:text-slate-500" title="Too early to measure">—</span>}
      </td>
      <td class="px-4 py-2.5 text-right tabular-nums text-slate-600 dark:text-slate-300">{hasData ? formatRate(blast.ClickToOpenRatePercent) : '—'}</td>
      <td class="px-4 py-2.5 text-right tabular-nums text-slate-400 dark:text-slate-500">{hasData ? formatRate(blast.OpenRatePercent) : '—'}</td>
    </tr>
  )
}

function Measure({ value, label, size = 'md', privileged = false, muted = false, caveat }: {
  value: string; label: string; size?: 'md' | 'lg'; privileged?: boolean; muted?: boolean; caveat?: string
}) {
  const valueClass = privileged
    ? `${size === 'lg' ? 'text-3xl' : 'text-2xl'} font-semibold tabular-nums text-primary-700 dark:text-primary-400`
    : muted
    ? 'text-xl font-medium tabular-nums text-slate-500 dark:text-slate-400'
    : 'text-2xl font-semibold tabular-nums text-slate-900 dark:text-white'
  const labelClass = privileged
    ? 'text-xs uppercase tracking-wide text-primary-700/70 dark:text-primary-400/70'
    : 'text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500'
  return (
    <div class="flex items-baseline gap-1.5">
      <span class={valueClass}>{value}</span>
      <span class={caveat ? `${labelClass} underline decoration-dotted underline-offset-2 cursor-help` : labelClass} title={caveat}>
        {label}{caveat ? ' ⓘ' : ''}
      </span>
    </div>
  )
}

function Divider() {
  return <div class="h-9 w-px bg-slate-200 dark:bg-slate-700/70" aria-hidden="true" />
}

function Legend({ swatch, label }: { swatch: 'bar' | 'opens' | 'clicks'; label: string }) {
  const mark = swatch === 'bar'
    ? <span class="inline-block h-2 w-3 rounded-sm bg-slate-300 dark:bg-slate-600" />
    : swatch === 'opens'
    ? <span class="inline-block h-0.5 w-3 bg-indigo-400" />
    : <span class="inline-block h-0.5 w-3 bg-primary-600" />
  const tone = swatch === 'clicks' ? 'text-primary-700 dark:text-primary-400' : 'text-slate-400 dark:text-slate-500'
  return <span class={`inline-flex items-center gap-1.5 ${tone}`}>{mark}{label}</span>
}

function formatSendDate(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? 'Broadcast' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}
